import { Bike, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Download, Eye, Plus, Search, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import APILink from '@/components/shared/api-link';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useFleet } from '@/hooks/api/use-fleet';
import { formatDate } from '@/lib/dates';
import { isWakamContract } from '@/lib/contracts';
import type { Contract } from '@/types/api/contract';
import { PDF } from '@/types/api/endpoints';
import { formatImmatriculation } from '@/lib/helpers';

export interface FlotteProps {
    contract: Contract;
    /** Classe historique passée par la fiche contrat (non appliquée). */
    className?: string;
}

type FilterField = 'sort' | 'immat';

export default function Flotte({ contract }: FlotteProps) {
    const contract_id = contract.id;
    const isWakam = isWakamContract(contract);

    const textInputRef = useRef<HTMLInputElement>(null);
    const tableTopRef = useRef<HTMLDivElement>(null);
    const [state, setState] = useState({
        query: '',
        sort: '',
        immat: '',
    });
    /* Page demandée, `null` pour la liste de départ. */
    const [page, setPage] = useState<number | null>(null);

    const fleetQuery = useFleet(contract_id, { page: page ?? undefined, sort: state.sort, immat: state.immat });
    const loading = fleetQuery.isFetching;
    const { data = [], meta = {} } = fleetQuery.data ?? {};
    /* Les comparaisons de pagination restent fausses tant que `meta` est incomplet, comme avec `undefined`. */
    const total = meta.total ?? NaN;
    const perPage = meta.per_page ?? NaN;
    const currentPage = meta.current_page ?? NaN;
    const lastPage = meta.last_page ?? NaN;

    const handleChangeSort = (value: string, field: FilterField) => {
        const newValue = formatImmatriculation(value.replaceAll(' ', ''));
        const newState = { ...state, [field]: newValue };
        setState(newState);
        setPage(null);
    };

    const handleChangePage = (target: number) => {
        setPage(target);
        tableTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <Card ref={tableTopRef} style={{ scrollMarginTop: '1rem' }}>
            <CardHeader className="pb-6 border-b">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Bike className="w-5 h-5 text-primary" />
                            Vos véhicules assurés
                        </CardTitle>
                        <p className="text-sm text-muted-foreground mt-1">
                            {loading ? 'Chargement…' : `${meta.total ?? 0} véhicule${(meta.total ?? 0) > 1 ? 's' : ''}`}
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {isWakam && contract.avenant_link && (
                            <Button asChild size="sm" variant="outline">
                                <a href={contract.avenant_link} target="_blank" rel="noopener noreferrer">
                                    Ajout / Changement de garanties
                                </a>
                            </Button>
                        )}
                        {!isWakam && (
                            <Button asChild size="sm">
                                <Link to={`/mes-contrats/mon-contrat/${contract_id}/ajout-vehicule`}>
                                    <Plus className="w-4 h-4" />
                                    Ajouter un véhicule
                                </Link>
                            </Button>
                        )}
                        <Button asChild size="sm" variant="destructive">
                            <Link to={`/mes-contrats/mon-contrat/${contract_id}/sortie-vehicules`}>
                                <Trash2 className="w-4 h-4" />
                                Sortir des véhicules
                            </Link>
                        </Button>
                        <Button asChild size="sm" variant="outline">
                            <APILink href={PDF.vehiclesExport(contract.id)} download rel="noopener noreferrer">
                                <Download className="w-4 h-4" />
                                Extraire la liste
                            </APILink>
                        </Button>
                    </div>
                </div>
            </CardHeader>

            <CardContent className="pt-6 space-y-6">
                {/* Filtres */}
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto] gap-2">
                    <div>
                        <label htmlFor="flotte-sort" className="sr-only">Trier par</label>
                        <select
                            id="flotte-sort"
                            value={state.sort}
                            onChange={(e) => handleChangeSort(e.target.value, 'sort')}
                            className="border-input bg-transparent flex h-9 w-full rounded-md border px-4 py-1 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]"
                        >
                            <option value="">Trier par adhésion</option>
                            <option value="date">Mise en circulation (croissant)</option>
                            <option value="-date">Mise en circulation (décroissant)</option>
                        </select>
                    </div>
                    <div className="relative">
                        <label htmlFor="flotte-immat" className="sr-only">Rechercher par immatriculation</label>
                        <Input
                            id="flotte-immat"
                            type="text"
                            ref={textInputRef}
                            placeholder="Rechercher par immatriculation"
                            className="pr-9 uppercase"
                            onKeyDown={(e) =>
                                e.key === 'Enter' && handleChangeSort((textInputRef.current?.value ?? '').toUpperCase(), 'immat')
                            }
                            onChange={(e) => e.target.value.length === 0 && handleChangeSort('', 'immat')}
                        />
                        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                    </div>
                    <Button
                        type="button"
                        size="sm"
                        className="h-9"
                        onClick={() => handleChangeSort((textInputRef.current?.value ?? '').toUpperCase(), 'immat')}
                    >
                        Rechercher
                    </Button>
                </div>

                {/* Tableau */}
                {loading ? (
                    <div className="flex items-center justify-center py-12" role="status" aria-live="polite">
                        <span className="h-6 w-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        <span className="sr-only">Chargement…</span>
                    </div>
                ) : data.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-12">
                        Aucun véhicule trouvé.
                    </p>
                ) : (
                    <div className="rounded-md border">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-muted/50">
                                    <TableHead>Adhésion</TableHead>
                                    <TableHead>Marque, Modèle</TableHead>
                                    <TableHead>Immatriculation</TableHead>
                                    <TableHead>Mise en circulation</TableHead>
                                    <TableHead>Statut</TableHead>
                                    <TableHead className="text-right">
                                        <span className="sr-only">Détails</span>
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {data.map((v) => (
                                    <TableRow key={v.adhesion}>
                                        <TableCell className="font-medium">{v.adhesion}</TableCell>
                                        <TableCell>{`${v.marque || ''} ${v.modele || ''}`.trim() || '—'}</TableCell>
                                        <TableCell>
                                            {v.immatriculation ? (
                                                <span className="font-mono text-xs uppercase">{v.immatriculation}</span>
                                            ) : (
                                                '—'
                                            )}
                                        </TableCell>
                                        <TableCell>{v.dateMEC ? formatDate(v.dateMEC) : '—'}</TableCell>
                                        <TableCell>
                                            <Badge variant={v.inactif ? 'secondary' : 'success'}>
                                                {v.inactif ? 'Inactif' : 'Actif'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button asChild variant="ghost" size="sm">
                                                <Link to={`/mes-contrats/mon-contrat/${contract_id}/vehicule/${v.adhesion}`}>
                                                    <Eye className="w-4 h-4" />
                                                    Voir
                                                </Link>
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}

                {/* Pagination */}
                {!loading && total > perPage && (
                    <nav className="flex items-center justify-center gap-1" aria-label="Pagination">
                        {currentPage > 2 && (
                            <Button variant="ghost" size="icon" onClick={() => handleChangePage(1)} aria-label="Première page">
                                <ChevronsLeft className="w-4 h-4" />
                            </Button>
                        )}
                        {currentPage > 1 && (
                            <Button variant="ghost" size="icon" onClick={() => handleChangePage(currentPage - 1)} aria-label="Page précédente">
                                <ChevronLeft className="w-4 h-4" />
                            </Button>
                        )}
                        <Button variant="outline" size="sm" aria-current="page" className="cursor-default">
                            Page {meta.current_page} / {meta.last_page}
                        </Button>
                        {lastPage > currentPage && (
                            <Button variant="ghost" size="icon" onClick={() => handleChangePage(currentPage + 1)} aria-label="Page suivante">
                                <ChevronRight className="w-4 h-4" />
                            </Button>
                        )}
                        {lastPage >= currentPage + 2 && (
                            <Button variant="ghost" size="icon" onClick={() => handleChangePage(lastPage)} aria-label="Dernière page">
                                <ChevronsRight className="w-4 h-4" />
                            </Button>
                        )}
                    </nav>
                )}
            </CardContent>
        </Card>
    );
}
